import { ref, computed } from 'vue';

/**
 * useSgaRenovacionDescuentos
 *
 * Manages discount state for SGA renewal modal.
 * Handles pre-assigned discounts from renewal-info and discount code verification.
 *
 * @param {Object} config - Configuration object
 * @param {Array} config.discounts - Available discounts from renewal-info
 * @param {String|Number} config.abonado_id - Subscriber ID for code verification
 * @param {Object} config.store - Vuex store
 * @param {Object} config.config - Config object with getVuexAlias method
 * @param {Object} config.toast - Toast service
 * @param {Function} config.t - i18n translate function
 *
 * @returns {Object} Reactive discount state and methods
 */
export default function useSgaRenovacionDescuentos(config) {
    const { discounts, abonado_id, store, config: appConfig, toast, t } = config;

    // Reactive state
    const selectedDiscounts = ref([]);
    const verifiedCodeDiscount = ref(null);
    const codeInputValue = ref('');
    const codeVerificationState = ref('idle'); // 'idle' | 'verifying' | 'success' | 'error'
    const codeErrorMessage = ref('');

    // Computed
    const codeVerifyLoading = computed(() => codeVerificationState.value === 'verifying');

    const availableDiscounts = computed(() => {
        if (discounts.length > 3 && process.env.NODE_ENV === 'development') {
            console.warn(`SGA devolvió ${discounts.length} descuentos, mostrando solo 3`);
        }
        return discounts.slice(0, 3);
    });

    const allActiveDiscounts = computed(() => {
        const active = [...selectedDiscounts.value];
        if (verifiedCodeDiscount.value) {
            active.push(verifiedCodeDiscount.value);
        }
        return active;
    });

    /**
     * Toggle pre-assigned discount selection by index.
     */
    const toggleDiscount = discountIndex => {
        const discount = availableDiscounts.value[discountIndex];
        if (!discount) return;

        const index = selectedDiscounts.value.findIndex(
            d => d.id === discount.id
        );

        if (index > -1) {
            selectedDiscounts.value.splice(index, 1);
        } else {
            selectedDiscounts.value.push(discount);
        }
    };

    /**
     * Check if a pre-assigned discount is selected by index.
     */
    const isDiscountSelected = discountIndex => {
        const discount = availableDiscounts.value[discountIndex];
        if (!discount) return false;
        return selectedDiscounts.value.some(d => d.id === discount.id);
    };

    /**
     * Verify discount code via API.
     */
    const verifyCode = async() => {
        const code = codeInputValue.value.trim();
        if (!code) return;

        if (codeVerificationState.value === 'verifying') return;

        codeVerificationState.value = 'verifying';
        codeErrorMessage.value = '';

        try {
            const response = await store.dispatch(
                appConfig.getVuexAlias('sga.profile.abonos.renewal-discounts.verify'),
                { abonado_id, codigo: code }
            );

            const discountData = response.data?.data || null;
            if (discountData && discountData.id) {
                verifiedCodeDiscount.value = {
                    ...discountData,
                    code
                };
                codeVerificationState.value = 'success';
                toast.success(t('perfil_sga.cupon_aplicado'));
            } else {
                codeVerificationState.value = 'error';
                codeErrorMessage.value = t('perfil_sga.cupon_invalido');
            }
        } catch (err) {
            codeVerificationState.value = 'error';
            const status = err.response?.status;
            if (status === 400 || status === 404) {
                codeErrorMessage.value = t('perfil_sga.cupon_invalido');
            } else {
                codeErrorMessage.value = t('perfil_sga.cupon_error');
            }
        }
    };

    /**
     * Clear the verified code discount.
     */
    const clearVerifiedCode = () => {
        verifiedCodeDiscount.value = null;
        codeInputValue.value = '';
        codeVerificationState.value = 'idle';
        codeErrorMessage.value = '';
    };

    /**
     * Reset all discount state (called on modal close).
     */
    const resetDiscounts = () => {
        selectedDiscounts.value = [];
        verifiedCodeDiscount.value = null;
        codeInputValue.value = '';
        codeVerificationState.value = 'idle';
        codeErrorMessage.value = '';
    };

    return {
        selectedDiscounts,
        verifiedCodeDiscount,
        codeInputValue,
        codeVerificationState,
        codeErrorMessage,
        codeVerifyLoading,
        availableDiscounts,
        allActiveDiscounts,
        toggleDiscount,
        isDiscountSelected,
        verifyCode,
        clearVerifiedCode,
        resetDiscounts
    };
}
